import { Link } from 'react-router';

import { Principes } from '@/components/Principes';
import { PublicationList, PublicationRow } from '@/components/publications/PublicationRow';
import { ReleveSection } from '@/components/publications/ReleveSection';
import { Accent } from '@/components/ui/Accent';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Container } from '@/components/ui/Container';
import { Label } from '@/components/ui/Label';
import { Section } from '@/components/ui/Section';
import { ArrowLink, SectionHead } from '@/components/ui/SectionHead';
import { Symbole } from '@/components/ui/Symbole';
import { latestReleve, publications } from '@/content/publications';
import {
  episodePath,
  episodes,
  findPhase,
  latestEpisode,
  phaseEpisodes,
  phases,
  published,
  type Episode,
} from '@/content/serie';
import { site } from '@/content/site';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { cn } from '@/lib/cn';

export function HomePage() {
  useDocumentMeta({
    title: `${site.name}, un projet, une série`,
    description: site.description,
    path: '/',
  });

  // Les deux épisodes qui précèdent le dernier, pour la colonne « À la une ».
  const previous = [...published]
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
    .slice(1, 2);

  return (
    <>
      <Hero />

      {latestEpisode && (
        <Container className="grid gap-6 desk:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <FeatureCard episode={latestEpisode} big />
          <div className="grid gap-6 tab:grid-cols-2 desk:grid-cols-1">
            {previous.map((episode) => (
              <FeatureCard key={episode.slug} episode={episode} />
            ))}
            {latestReleve && (
              <Link
                to="/blog#releve"
                className="group flex flex-col overflow-hidden rounded-xl bg-blanc text-encre no-underline"
              >
                <div className="flex aspect-[16/7] items-center justify-center bg-ciel">
                  <Symbole accent="var(--color-blanc)" className="aspect-square h-auto w-[18%]" />
                </div>
                <div className="flex flex-1 flex-col gap-2.5 px-7 pt-6 pb-7">
                  <Label>Le Relevé, semaine {latestReleve.semaine}</Label>
                  <h3 className="text-h3 group-hover:underline group-hover:decoration-ciel group-hover:decoration-2 group-hover:underline-offset-[5px]">
                    {upperFirst(latestReleve.title)}
                  </h3>
                  <Label className="mt-auto pt-1.5">{latestReleve.date}</Label>
                </div>
              </Link>
            )}
          </div>
        </Container>
      )}

      <Section aria-labelledby="publications-titre">
        <Container>
          <SectionHead
            id="publications-titre"
            title="Dernières publications"
            aside={<ArrowLink to="/blog">Tout le blog</ArrowLink>}
          />
          <PublicationList>
            {publications
              .filter((publication) => publication.to)
              .slice(0, 5)
              .map((publication) => (
                <PublicationRow
                  key={`${publication.kind}-${publication.date}`}
                  publication={publication}
                />
              ))}
          </PublicationList>
        </Container>
      </Section>

      <FilRouge />

      {latestReleve && (
        <ReleveSection
          releve={latestReleve}
          eyebrow={`Le Relevé hebdomadaire, semaine ${latestReleve.semaine}`}
          action={<ButtonLink to="/blog">Archives du Relevé</ButtonLink>}
        />
      )}

      <Section id="principes" aria-labelledby="principes-titre">
        <Container>
          <SectionHead
            id="principes-titre"
            title={
              <>
                Un labo personnel,
                <br />
                des chiffres mesurés.
              </>
            }
            aside={<ArrowLink to="/a-propos">À propos de moi</ArrowLink>}
          />
          <Principes />
        </Container>
      </Section>
    </>
  );
}

function Hero() {
  const total = episodes.length;

  return (
    <section aria-labelledby="hero-titre" className="pt-14 pb-15 tab:pt-26 tab:pb-24">
      <Container className="grid items-center gap-8 tab:grid-cols-[minmax(0,1fr)_180px] desk:grid-cols-[minmax(0,1fr)_300px] desk:gap-14">
        <div>
          <h1 id="hero-titre" className="max-w-[10.5em] text-display">
            <Accent text="Rien n'est publié qui n'ait |tourné." />
          </h1>

          <div className="mt-13 grid items-end gap-12 desk:grid-cols-[minmax(0,1fr)_auto]">
            <p className="max-w-[31em] text-lead text-encre-soft">{site.description}</p>
            <div className="flex flex-wrap gap-3">
              {latestEpisode && (
                <ButtonLink to={episodePath(latestEpisode)} icon="play">
                  Regarder le dernier épisode
                </ButtonLink>
              )}
              <ButtonLink to="/serie" variant="ligne">
                Toute la série
              </ButtonLink>
            </div>
          </div>

          <div className="mt-14 flex items-center gap-4.5 border-t border-line pt-5.5">
            <div
              className="grid max-w-[520px] flex-1 gap-1.5"
              style={{ gridTemplateColumns: `repeat(${String(total)}, 1fr)` }}
              aria-hidden="true"
            >
              {episodes.map((episode) => (
                <i
                  key={episode.num}
                  className={cn(
                    'h-1.5 rounded-xs',
                    episode.status === 'publie' ? 'bg-ciel' : 'bg-blanc',
                  )}
                />
              ))}
            </div>
            <Label>Un épisode par mois</Label>
          </div>
        </div>

        {/* Sous 720 px, le symbole remonte au-dessus du titre, à 96 px. */}
        <div className="-order-1 flex justify-start tab:order-none tab:justify-center">
          <Symbole animated big className="aspect-square h-auto w-24 tab:w-full" />
        </div>
      </Container>
    </section>
  );
}

function FeatureCard({ episode, big = false }: { episode: Episode; big?: boolean }) {
  const phase = findPhase(episode.phase);

  return (
    <Link
      to={episodePath(episode)}
      className="group flex flex-col overflow-hidden rounded-xl bg-blanc text-encre no-underline"
    >
      <div
        className={cn(
          'flex items-center justify-center',
          big ? 'aspect-[16/8] bg-encre text-blanc' : 'aspect-[16/7] bg-ciel/30',
        )}
      >
        <Symbole className={cn('aspect-square h-auto', big ? 'w-[22%]' : 'w-[18%]')} />
      </div>
      <div className="flex flex-1 flex-col gap-2.5 px-7 pt-6 pb-7">
        <Label>
          Épisode {episode.num}, {phase?.title.toLowerCase()}
        </Label>
        <h3
          className={cn(
            'group-hover:underline group-hover:decoration-ciel group-hover:decoration-2 group-hover:underline-offset-[5px]',
            big ? 'text-h3-lg' : 'text-h3',
          )}
        >
          {episode.title}
        </h3>
        {big && episode.summary && (
          <p className="text-body-s text-encre-soft">{episode.summary}</p>
        )}
        <Label className="mt-auto pt-1.5">
          {[
            episode.date,
            big && episode.readingTime && `${String(episode.readingTime)} min`,
            big && episode.videoMinutes && `vidéo ${String(episode.videoMinutes)} min`,
          ]
            .filter(Boolean)
            .join(', ')}
        </Label>
      </div>
    </Link>
  );
}

/** Bande encre : les six phases et l'avancement de chacune. */
function FilRouge() {
  return (
    <Section tone="encre" aria-labelledby="fil-rouge-titre">
      <Container>
        <SectionHead
          onEncre
          id="fil-rouge-titre"
          eyebrow="Le fil rouge"
          className="mb-0"
          title={
            <>
              Un projet qui grandit
              <br />
              d&apos;épisode en <Accent text="|épisode." variant="ciel" />
            </>
          }
          aside={
            <ButtonLink to="/serie" variant="ciel">
              La série complète →
            </ButtonLink>
          }
        />

        <ol className="m-0 mt-14 grid list-none grid-cols-2 border-t border-blanc/20 p-0 tab:grid-cols-3 desk:grid-cols-6">
          {phases.map((phase) => (
            <li
              key={phase.id}
              className="flex flex-col gap-2.5 border-b border-blanc/12 px-4.5 py-5.5 desk:border-b-0 desk:pt-6 desk:pb-0 desk:pl-4.5 desk:first:pl-0 desk:not-first:border-l desk:not-first:border-blanc/12"
            >
              <Label tone="clair">{phase.num}</Label>
              <b className="text-h6 font-medium">{phase.title}</b>
              <p className="text-body-2xs text-blanc/72">{phase.tools}</p>
              <div className="mt-1 flex gap-1" aria-hidden="true">
                {phaseEpisodes(phase.id).map((episode) => (
                  <i
                    key={episode.num}
                    className={cn(
                      'h-1 flex-1 rounded-xs',
                      episode.status === 'publie' ? 'bg-ciel' : 'bg-blanc/16',
                    )}
                  />
                ))}
              </div>
            </li>
          ))}
        </ol>
      </Container>
    </Section>
  );
}

const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
